import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Pencil, Plus, Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  name: "", scope: "group", vehicle_group_id: "", vehicle_ids: [],
  mode: "percent", value: 10, starts_at: "", ends_at: "", sort_order: 0, active: true,
};

const MODE_LABEL = { fixed_price: "Fester Preis/Tag", percent: "Rabatt in %", fixed_amount: "Rabatt in €" };

function modeValueText(s) {
  if (s.mode === "fixed_price") return `${Number(s.value).toFixed(2)}€/Tag`;
  if (s.mode === "percent") return `-${Number(s.value).toFixed(0)}%`;
  return `-${Number(s.value).toFixed(2)}€`;
}

function isActiveNow(s) {
  if (!s.active) return false;
  const today = new Date().toISOString().slice(0, 10);
  if (s.starts_at && s.starts_at > today) return false;
  if (s.ends_at && s.ends_at < today) return false;
  return true;
}

function SpecialDialog({ open, onClose, editing, groups, vehicles, onSave }) {
  const [form, setForm] = useState(editing || { ...EMPTY });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(editing ? { ...EMPTY, ...editing } : { ...EMPTY });
  }, [editing]);

  const f = (k) => (v) => setForm((p) => ({ ...p, [k]: v }));

  const toggleVehicle = (id) => {
    setForm((p) => ({
      ...p,
      vehicle_ids: p.vehicle_ids.includes(id) ? p.vehicle_ids.filter((x) => x !== id) : [...p.vehicle_ids, id],
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Name ist Pflichtfeld"); return; }
    if (form.scope === "group" && !form.vehicle_group_id) { toast.error("Bitte eine Fahrzeuggruppe wählen"); return; }
    if (form.scope === "vehicles" && form.vehicle_ids.length === 0) { toast.error("Bitte mindestens ein Fahrzeug wählen"); return; }
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? "Special bearbeiten" : "Special anlegen"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label>Name *</Label>
            <Input value={form.name} onChange={(e) => f("name")(e.target.value)}
              className="mt-1" placeholder="z.B. Sommeraktion Kompaktklasse" data-testid="special-name" />
          </div>

          <div>
            <Label>Gilt für</Label>
            <Select value={form.scope} onValueChange={(v) => f("scope")(v)}>
              <SelectTrigger className="mt-1" data-testid="special-scope"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="group">Fahrzeuggruppe</SelectItem>
                <SelectItem value="vehicles">Bestimmte Fahrzeuge</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {form.scope === "group" ? (
            <div>
              <Label>Fahrzeuggruppe</Label>
              <Select value={form.vehicle_group_id || ""} onValueChange={(v) => f("vehicle_group_id")(v)}>
                <SelectTrigger className="mt-1" data-testid="special-group"><SelectValue placeholder="Gruppe wählen" /></SelectTrigger>
                <SelectContent>
                  {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div>
              <Label>Fahrzeuge</Label>
              <div className="mt-1 max-h-44 overflow-y-auto border border-[#E5E5E5] rounded-md p-2 flex flex-wrap gap-1.5" data-testid="special-vehicles">
                {vehicles.length === 0 && <p className="text-xs text-[#525252] p-2">Keine Fahrzeuge vorhanden.</p>}
                {vehicles.map((v) => {
                  const on = form.vehicle_ids.includes(v.id);
                  return (
                    <button
                      key={v.id} type="button" onClick={() => toggleVehicle(v.id)}
                      className={`text-xs px-2.5 py-1 rounded-sm border transition ${
                        on ? "bg-[#E11226] text-white border-[#E11226]" : "bg-white text-[#262626] border-[#E5E5E5] hover:border-[#A3A3A3]"
                      }`}
                      data-testid={`special-vehicle-${v.id}`}
                    >
                      {v.brand} {v.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Art</Label>
              <Select value={form.mode} onValueChange={(v) => f("mode")(v)}>
                <SelectTrigger className="mt-1" data-testid="special-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(MODE_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{form.mode === "fixed_price" ? "Preis/Tag (€)" : form.mode === "percent" ? "Rabatt (%)" : "Rabatt (€)"}</Label>
              <Input type="number" step="0.5" value={form.value} onChange={(e) => f("value")(parseFloat(e.target.value) || 0)}
                className="mt-1" data-testid="special-value" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Start (optional)</Label>
              <Input type="date" value={form.starts_at || ""} onChange={(e) => f("starts_at")(e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label>Ende (optional)</Label>
              <Input type="date" value={form.ends_at || ""} onChange={(e) => f("ends_at")(e.target.value)} className="mt-1" />
            </div>
          </div>

          <div>
            <Label>Reihenfolge</Label>
            <Input type="number" value={form.sort_order} onChange={(e) => f("sort_order")(parseInt(e.target.value) || 0)} className="mt-1" />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="special-active-chk" checked={form.active}
              onChange={(e) => f("active")(e.target.checked)} className="accent-[#E11226]" />
            <Label htmlFor="special-active-chk">Aktiv</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#E11226] hover:bg-[#C20E1F]" data-testid="special-save">
            {saving ? "Speichern…" : "Speichern"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminSpecials() {
  const [specials, setSpecials] = useState([]);
  const [groups, setGroups] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [dialog, setDialog] = useState(null);

  const load = async () => {
    const [sp, grps, vs] = await Promise.all([
      api.get("/admin/specials"),
      api.get("/admin/vehicle-groups"),
      api.get("/admin/vehicles"),
    ]);
    setSpecials(sp.data);
    setGroups(grps.data);
    setVehicles(vs.data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => setDialog({ editing: null });
  const openEdit = (item) => setDialog({ editing: item });
  const closeDialog = () => setDialog(null);

  const handleSave = async (form) => {
    if (dialog.editing) {
      await api.put(`/admin/specials/${dialog.editing.id}`, form);
      toast.success("Special aktualisiert");
    } else {
      await api.post("/admin/specials", form);
      toast.success("Special angelegt");
    }
    await load();
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`"${item.name}" wirklich löschen?`)) return;
    try {
      await api.delete(`/admin/specials/${item.id}`);
      toast.success("Gelöscht");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const scopeLabel = (s) => {
    if (s.scope === "group") return groups.find((g) => g.id === s.vehicle_group_id)?.name || "—";
    return `${s.vehicle_ids.length} Fahrzeug${s.vehicle_ids.length !== 1 ? "e" : ""}`;
  };

  return (
    <div className="rf-fade-in" data-testid="admin-specials">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[#E11226] font-semibold">Marketing</div>
          <h1 className="font-display text-3xl font-bold text-[#0A0A0A] mt-1">Specials & Sonderpreise</h1>
        </div>
        <Button onClick={openNew} className="bg-[#E11226] hover:bg-[#C20E1F]" data-testid="special-new-btn">
          <Plus className="w-4 h-4 mr-2" /> Special anlegen
        </Button>
      </div>

      <div className="bg-white border border-[#E5E5E5] rounded-lg overflow-hidden">
        {specials.length === 0 ? (
          <p className="text-[#525252] text-sm py-6 text-center">Noch keine Specials angelegt.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F4F4F4] text-xs uppercase tracking-wider text-[#525252]">
                <tr>
                  <th className="text-left p-3">Name</th>
                  <th className="text-left p-3">Gilt für</th>
                  <th className="text-left p-3">Rabatt</th>
                  <th className="text-left p-3">Zeitraum</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-right p-3">Aktionen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E5E5E5]">
                {specials.map((s) => (
                  <tr key={s.id} className="hover:bg-[#FAFAFA]" data-testid={`admin-special-${s.id}`}>
                    <td className="p-3 font-semibold text-[#0A0A0A]">
                      <div className="flex items-center gap-1.5"><Sparkles size={13} className="text-[#E11226]" /> {s.name}</div>
                    </td>
                    <td className="p-3 text-[#525252]">
                      {s.scope === "group" ? "Gruppe" : "Fahrzeuge"} · {scopeLabel(s)}
                    </td>
                    <td className="p-3 font-semibold text-[#E11226]">{modeValueText(s)}</td>
                    <td className="p-3 text-[#525252] text-xs">
                      {s.starts_at || s.ends_at ? `${s.starts_at || "…"} → ${s.ends_at || "…"}` : "Unbefristet"}
                    </td>
                    <td className="p-3">
                      <Badge className={isActiveNow(s) ? "bg-emerald-100 text-emerald-800 border-0" : "bg-[#F4F4F4] text-[#525252] border-0"}>
                        {isActiveNow(s) ? "Aktiv" : s.active ? "Inaktiv (Zeitraum)" : "Deaktiviert"}
                      </Badge>
                    </td>
                    <td className="p-3">
                      <div className="flex gap-2 justify-end">
                        <Button size="icon" variant="ghost" onClick={() => openEdit(s)} data-testid={`special-edit-${s.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button size="icon" variant="ghost" className="text-red-500 hover:text-red-400" onClick={() => handleDelete(s)} data-testid={`special-delete-${s.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {dialog && (
        <SpecialDialog open onClose={closeDialog} editing={dialog.editing} groups={groups} vehicles={vehicles} onSave={handleSave} />
      )}
    </div>
  );
}
